import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "/en/heritage";
const SV_PATH = "/heritage";

const TRACKS = [
  {
    eyebrow: "For families and individuals",
    title: "Life Stories",
    body:
      "A filmed interview where a person tells the story of their life — upbringing, family, work, experiences and the memories that matter — preserved for children, grandchildren and generations to come.",
    to: "/en/heritage/livsberattelser" as const,
    cta: "Read about Life Stories",
  },
  {
    eyebrow: "For companies and organisations",
    title: "Knowledge Legacy",
    body:
      "Structured filmed interviews with key people, founders, senior staff or anyone carrying critical experience. Used for onboarding, internal training, culture work and documentation.",
    to: "/en/heritage/kunskapsarv" as const,
    cta: "Read about Knowledge Legacy",
  },
];

const PROCESS = [
  { label: "First conversation", body: "We start by understanding the purpose, who will be interviewed and how the material will be used." },
  { label: "Planning and question outline", body: "We design the structure and question areas so the interview feels clear and safe." },
  { label: "Filmed interview", body: "The interview is recorded in a calm setting where the person can speak at their own pace." },
  { label: "Editing and post-production", body: "Material is cut into a main film or shorter chapters. Sound, colour and structure are crafted professionally." },
  { label: "Delivery", body: "The film is delivered digitally — ready to be shared privately within the family or used internally in the organisation." },
];

const USE_CASES_PERSONAL = [
  "Interview with a parent or grandparent",
  "Family history",
  "Personal portrait",
  "Memorial film",
  "Filmed message to the family",
  "Documentation for the future",
];

const USE_CASES_BUSINESS = [
  "Knowledge transfer before retirement",
  "Interview with a founder",
  "Documenting company culture",
  "Onboarding material",
  "Internal training",
  "Generational handover",
  "Company history",
  "Documentation of methods and experience",
];

const PACKAGES_PERSONAL = [
  { name: "The Life Interview", body: "A smaller format: one filmed interview with one person. Pre-meeting, question outline, filmed interview, editing and digital delivery." },
  { name: "The Family Legacy", body: "A more extensive format with a longer interview, chapter structure and the option to weave in photos or archive material." },
  { name: "Legacy", body: "For families who want to build a more comprehensive filmed legacy with multiple interviews, clear narrative structure, archive material, main film and shorter clips." },
];

const PACKAGES_BUSINESS = [
  { name: "Key Person Interview", body: "For a senior employee, specialist, leader or founder. Pre-meeting, interview design, filmed interview, editing and internal delivery." },
  { name: "Knowledge Transfer", body: "For organisations that need to document tacit knowledge, methods and experience. A filmed interview or interview series with chaptered material for onboarding and internal training." },
  { name: "Company Legacy", body: "For companies that want to document history, culture, founder journey or generational handover. Interviews with several people, a summary film and shorter internal clips." },
];

const VALUE_PERSONAL = [
  "Preserves stories directly from the person themselves",
  "Lets future generations experience the voice, the memories and the story",
  "A more living alternative to text, photos or genealogy",
  "Can be shared privately within the family",
  "Makes it easier to ask the questions while there is still time",
];

const VALUE_BUSINESS = [
  "Reduces the risk of losing critical experience when people leave the organisation",
  "Supports onboarding and internal training",
  "Documents culture, values and working methods",
  "Preserves founder stories and company memory",
  "Useful for generational handovers, retirements and anniversaries",
  "Makes tacit knowledge accessible to more people in the organisation",
];

const FAQ = [
  { q: "What is Fenny Heritage?", a: "Fenny Heritage is a service for filmed interviews where individuals, families, companies and organisations can document life stories, experience and knowledge in professional form." },
  { q: "Who is the service for?", a: "Families who want to preserve a loved one's story, and companies or organisations that want to document experience, company history, culture and tacit knowledge." },
  { q: "What is the difference between Life Stories and Knowledge Legacy?", a: "Life Stories are private interviews about a person's life, intended for the family. Knowledge Legacy is structured interviews inside organisations to document experience, methods and company history." },
  { q: "How long is the film?", a: "It depends on the scope and purpose. A single main film is typically 15–45 minutes, often complemented by shorter excerpts or chapters that are easier to share and watch." },
  { q: "Where is the interview recorded?", a: "In a calm setting that suits the person — at home, at the office, in a studio or in a place with personal meaning. We bring all equipment and build a safe recording environment." },
  { q: "Can we contribute our own questions?", a: "Yes. The question outline is developed together. We suggest structure and areas, you bring what matters to you, and the person being interviewed can see the questions in advance if they wish." },
  { q: "Can you use old photos or archive material?", a: "Yes. In the larger packages we can weave in photos, documents, film or other archive material that complements the story." },
  { q: "Can the material stay private?", a: "Yes. The film is delivered digitally directly to you and is not shared further. You decide who gets to see it." },
  { q: "Can companies use the films internally?", a: "Yes. The business packages are designed for internal use — onboarding, training, culture work, anniversaries or intranet. External publication is also possible when appropriate." },
  { q: "How does consent and rights work?", a: "The person being interviewed gives written consent to recording and intended use. Rights to the finished film belong to you. We follow good documentary practice and let the person review the material before delivery." },
  { q: "How long does production take?", a: "From first conversation to finished film, the process usually takes 4–8 weeks, depending on scope, number of interviews and how much archive material is processed." },
  { q: "What does Fenny Heritage cost?", a: "Price depends on scope. We provide a quote after the first conversation. Smaller interview formats start in the lower price range for Prime productions; more extensive legacies are higher." },
];

export const Route = createFileRoute("/en_/heritage/")({
  head: () => ({
    meta: [
      { title: "Fenny Heritage — filmed life stories and knowledge legacy" },
      { name: "description", content: "Fenny Heritage helps families and companies preserve life stories, experience and tacit knowledge through professionally filmed interviews." },
      { property: "og:title", content: "Fenny Heritage — life stories and knowledge on film" },
      { property: "og:description", content: "Filmed interviews for families, companies and organisations. Preserve stories, experience and knowledge that should not be lost." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [
      { rel: "canonical", href: `https://fenny.se${PATH}` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/en/heritage", name: "Fenny Heritage — filmed life stories and knowledge legacy", description: "Fenny Heritage helps families and companies preserve life stories, experience and tacit knowledge through professionally filmed interviews."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Fenny Heritage",
            path: PATH,
            description:
              "Filmed interviews for life stories and knowledge legacy. Fenny helps families, companies and organisations document stories, experience and tacit knowledge.",
            serviceType: "Filmed interview and documentation",
            priceRange: "Quote after first conversation",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Home", path: "/en" },
            { name: "Heritage", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: HeritageHubEn,
});

function HeritageHubEn() {
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">Fenny Heritage</div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            Stories and knowledge that should not be lost
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl" data-speakable>
            Fenny Heritage helps families, companies and organisations document
            life stories, experience and tacit knowledge through professionally
            filmed interviews.
          </p>
          <QuickAnswer
            className="mt-6"
            text="Fenny Heritage is a service for filmed interviews where individuals, families, companies and organisations can document life stories, experience and knowledge in professional form."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/en/contact" className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90">
              Book a first conversation <ArrowRight className="size-4" />
            </Link>
            <a href="#process" className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]">
              See how it works
            </a>
          </div>
        </div>
      </Section>

      <Section className="py-8">
        <div className="max-w-3xl text-pretty text-base text-foreground/80 md:text-lg">
          <p>
            There are stories, experiences and knowledge that cannot be
            recreated afterwards. Heritage brings together two related needs:
            life stories for families and knowledge legacy for organisations.
            The shared core is the same — structured, filmed interviews made
            with craft and respect.
          </p>
          <p className="mt-4 text-foreground/70">
            Where Fenny helps companies tell their story going forward, Fenny
            Heritage helps people and organisations preserve what shaped them.
          </p>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Two tracks</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {TRACKS.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className="group flex flex-col rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7 transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
            >
              <div className="text-xs uppercase tracking-widest text-mint">{t.eyebrow}</div>
              <h3 className="mt-3 text-2xl font-semibold md:text-3xl">{t.title}</h3>
              <p className="mt-3 text-pretty text-foreground/80">{t.body}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-mint">
                {t.cta}
                <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Why Heritage exists</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-surface/40 p-6">
            <div className="text-xs uppercase tracking-widest text-mint">For individuals</div>
            <ul className="mt-4 space-y-3 text-foreground/85">
              {VALUE_PERSONAL.map((v) => (
                <li key={v} className="flex gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-surface/40 p-6">
            <div className="text-xs uppercase tracking-widest text-mint">For companies</div>
            <ul className="mt-4 space-y-3 text-foreground/85">
              {VALUE_BUSINESS.map((v) => (
                <li key={v} className="flex gap-3">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">How it works</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Examples of use</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">Life Stories</div>
            <ul className="mt-4 space-y-2 text-foreground/85">
              {USE_CASES_PERSONAL.map((u) => (<li key={u}>— {u}</li>))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-mint">Knowledge Legacy</div>
            <ul className="mt-4 space-y-2 text-foreground/85">
              {USE_CASES_BUSINESS.map((u) => (<li key={u}>— {u}</li>))}
            </ul>
          </div>
        </div>
      </Section>

      <Section className="py-10" id="packages">
        <h2 className="text-3xl font-semibold md:text-4xl">Packages</h2>
        <p className="mt-4 max-w-3xl text-foreground/75">
          Two lines — Heritage Personal for individuals and families, Heritage
          Business for companies and organisations. Pricing is provided as a
          quote after the first conversation.
        </p>

        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-mint">Heritage Personal</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {PACKAGES_PERSONAL.map((p) => (
              <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
                <div className="font-medium">{p.name}</div>
                <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-mint">Heritage Business</div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {PACKAGES_BUSINESS.map((p) => (
              <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
                <div className="font-medium">{p.name}</div>
                <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Frequently asked questions</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinksRail sourcePath={PATH} />
      <SignatureCta />
    </article>
  );
}
